import { Languages } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/shadcn/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/shadcn/dropdown-menu';
import { useRTL } from 'hooks';
import { cn } from '@/utils';

interface Props {
  className?: string;
}

const languages = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'he', name: 'עברית', flag: '🇮🇱' },
];

export function LanguageSwitcher({ className }: Props) {
  const { i18n, t } = useTranslation();
  const { isRTL } = useRTL();

  const handleLanguageChange = (languageCode: string) => {
    i18n.changeLanguage(languageCode);
  };

  const currentLanguage = languages.find((lang) => lang.code === i18n.language) || languages[0];

  return (
    <DropdownMenu dir={isRTL ? 'rtl' : 'ltr'}>
      <DropdownMenuTrigger className={className} asChild>
        <Button variant="ghost" size="sm" className="gap-2" aria-label={t('aria.changeLanguage')}>
          <Languages className="h-4 w-4" />
          <span className="hidden sm:inline">
            {currentLanguage.name}
          </span>
          <span className="sm:hidden">{currentLanguage.flag}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[150px]">
        {languages.map((language) => (
          <DropdownMenuItem
            key={language.code}
            onClick={() => handleLanguageChange(language.code)}
            className={cn(
              i18n.language === language.code && 'font-medium',
              'cursor-pointer',
            )}
            aria-label={t('aria.switchToLanguage', { language: language.name })}
          >
            <span
              className="me-2"
              role="img"
              aria-label={t('aria.languageFlag', { language: language.name })}
            >
              {language.flag}
            </span>
            {language.name}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
